import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { LocalDevice } from '@this+that/models';

const GET_TERMINAL_DEVICES = gql`
  query getMyLocalDevices {
    getMyLocalDevices {
      userId
      deviceId
      name
      lastSeenAt
      isConnected
    }
  }
`;

const CREATE_TERMINAL_TICKET = gql`
  mutation createTerminalTicket($deviceId: ID!) {
    createTerminalTicket(deviceId: $deviceId) {
      ticket
      expiresAt
    }
  }
`;

const CREATE_TERMINAL_ATTACHMENT_UPLOAD = gql`
  mutation createTerminalAttachmentUpload(
    $deviceId: ID!
    $fileName: String!
    $contentType: String!
    $size: Int!
  ) {
    createTerminalAttachmentUpload(
      deviceId: $deviceId
      fileName: $fileName
      contentType: $contentType
      size: $size
    ) {
      attachmentId
      uploadUrl
      uploadFields
      downloadUrl
      expiresAt
    }
  }
`;

const SEND_TERMINAL_MESSAGE = gql`
  mutation sendTerminalMessage($deviceId: ID!, $message: String!) {
    sendTerminalMessage(deviceId: $deviceId, message: $message)
  }
`;

interface GetTerminalDevicesData {
  getMyLocalDevices: LocalDevice[];
}

interface CreateTerminalTicketData {
  createTerminalTicket: { ticket: string; expiresAt: string };
}

export interface TerminalAttachmentUploadTicket {
  attachmentId: string;
  uploadUrl: string;
  uploadFields: string;
  downloadUrl: string;
  expiresAt: string;
}

interface CreateTerminalAttachmentUploadData {
  createTerminalAttachmentUpload: TerminalAttachmentUploadTicket;
}

export interface TerminalAttachmentUploadInput {
  deviceId: string;
  fileName: string;
  contentType: string;
  size: number;
}

export interface TerminalRequests {
  createTerminalAttachmentUpload: (
    input: TerminalAttachmentUploadInput,
  ) => Promise<TerminalAttachmentUploadTicket>;
  getTerminalDevices: () => Promise<LocalDevice[]>;
  createTerminalTicket: (deviceId: string) => Promise<string>;
  sendTerminalMessage: (deviceId: string, message: string) => Promise<void>;
}

export function useTerminalRequests(): TerminalRequests {
  const client = useApolloClient();

  return {
    getTerminalDevices: async () => {
      const { data } = await client.query<GetTerminalDevicesData>({
        query: GET_TERMINAL_DEVICES,
        fetchPolicy: 'network-only',
        errorPolicy: 'none',
      });
      return data?.getMyLocalDevices ?? [];
    },
    createTerminalTicket: async (deviceId) => {
      const { data } = await client.mutate<CreateTerminalTicketData>({
        mutation: CREATE_TERMINAL_TICKET,
        variables: { deviceId },
        errorPolicy: 'none',
      });
      if (!data) {
        throw new Error('createTerminalTicket returned no data');
      }
      return data.createTerminalTicket.ticket;
    },
    createTerminalAttachmentUpload: async (input) => {
      const { data } = await client.mutate<CreateTerminalAttachmentUploadData>({
        mutation: CREATE_TERMINAL_ATTACHMENT_UPLOAD,
        variables: input,
        errorPolicy: 'none',
      });
      if (!data) {
        throw new Error('createTerminalAttachmentUpload returned no data');
      }
      return data.createTerminalAttachmentUpload;
    },
    sendTerminalMessage: async (deviceId, message) => {
      await client.mutate({
        mutation: SEND_TERMINAL_MESSAGE,
        variables: { deviceId, message },
        errorPolicy: 'none',
      });
    },
  };
}
